import { useEffect, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  Bitcoin,
  CalendarClock,
  ChartLine,
  CreditCard,
  Landmark,
  Upload,
  Wallet,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendChart } from "../charts/TrendChart";
import { Logo } from "../components/Logo";
import {
  cadenceLabels,
  formatDate,
  formatMonth,
  formatSEK0,
} from "../lib/format";
import type {
  CryptoOverview,
  InvestmentsOverview,
  NetWorthOverview,
  RecurringOverview,
  SpendingSummary,
} from "../types";
import type { AssetsSection, Page } from "../App";

interface OverviewPageProps {
  onNavigate: (page: Page, assetsSection?: AssetsSection) => void;
}

interface OverviewData {
  netWorth: NetWorthOverview;
  recurring: RecurringOverview;
  investments: InvestmentsOverview;
  crypto: CryptoOverview;
  summary: SpendingSummary | null;
  month: string | null;
  budgetTotal: number;
}

export function OverviewPage({ onNavigate }: OverviewPageProps) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [netWorthRes, recurringRes, investRes, cryptoRes, monthsRes, budgetRes] =
          await Promise.all([
            fetch("/api/networth"),
            fetch("/api/recurring"),
            fetch("/api/investments"),
            fetch("/api/crypto"),
            fetch("/api/months"),
            fetch("/api/budget"),
          ]);

        const months: string[] = await monthsRes.json();
        const nowMonth = new Date().toISOString().slice(0, 7);
        const month = months.includes(nowMonth)
          ? nowMonth
          : months[months.length - 1] ?? null;

        let summary: SpendingSummary | null = null;
        if (month) {
          const summaryRes = await fetch(`/api/summary?month=${month}`);
          if (summaryRes.ok) summary = await summaryRes.json();
        }

        const budget = await budgetRes.json();
        const budgetTotal = Object.values(budget?.categories ?? {}).reduce(
          (sum: number, v) => sum + Number(v),
          0
        );

        if (!cancelled) {
          setData({
            netWorth: await netWorthRes.json(),
            recurring: await recurringRes.json(),
            investments: await investRes.json(),
            crypto: await cryptoRes.json(),
            summary,
            month,
            budgetTotal,
          });
        }
      } catch {
        if (!cancelled) setError("Kunde inte hämta översikten.");
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <div className="py-20 text-center text-muted-foreground">{error}</div>;
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const { netWorth, recurring, investments, crypto, summary, month, budgetTotal } = data;
  const current = netWorth.current;

  const isEmpty =
    !summary &&
    investments.holdings.length === 0 &&
    crypto.holdings.length === 0 &&
    current.assets === 0 &&
    current.liabilities === 0;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <Logo className="h-14 w-14 text-primary" />
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Välkommen till Klöver</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Hela familjens ekonomi, samlad. Börja med att ladda upp kontoutdrag,
            importera dina positioner eller lägga till konton.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={() => onNavigate("upload")}>
            <Upload className="mr-2 h-4 w-4" /> Ladda upp kontoutdrag
          </Button>
          <Button variant="outline" onClick={() => onNavigate("assets", "investments")}>
            <ChartLine className="mr-2 h-4 w-4" /> Importera positioner
          </Button>
          <Button variant="outline" onClick={() => onNavigate("assets", "accounts")}>
            <Landmark className="mr-2 h-4 w-4" /> Lägg till konton
          </Button>
        </div>
      </div>
    );
  }

  const history = netWorth.history.map((s) => ({ date: s.date, value: s.net }));
  const oldest = netWorth.history[0];
  const delta = oldest ? current.net - oldest.net : 0;

  const netThisMonth = summary ? summary.totalIncome - summary.totalSpending : 0;
  const budgetUsedPct =
    summary && budgetTotal > 0
      ? Math.min(Math.round((summary.totalSpending / budgetTotal) * 100), 999)
      : null;

  const upcoming = recurring.items
    .filter((i) => i.active && !i.ignored && i.nextDate)
    .sort((a, b) => (a.nextDate! < b.nextDate! ? -1 : 1))
    .slice(0, 4);

  const investDelta = investments.totalCostBasis > 0
    ? investments.totalValue - investments.totalCostBasis
    : null;

  const assetRows = [
    { label: "Bankkonton", value: current.breakdown.bankAccounts },
    { label: "Investeringar", value: current.breakdown.investments },
    { label: "Krypto", value: current.breakdown.crypto },
    { label: "Övriga tillgångar", value: current.breakdown.manualAssets },
  ].filter((r) => r.value !== 0);

  const liabilityRows = [
    { label: "Kreditkort", value: current.breakdown.creditCards },
    { label: "Lån & bolån", value: current.breakdown.loans },
    { label: "Övriga skulder", value: current.breakdown.otherLiabilities },
  ].filter((r) => r.value !== 0);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex items-baseline justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Översikt</h2>
        <span className="text-sm text-muted-foreground">
          {new Date().toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}
        </span>
      </div>

      {/* Net worth hero */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Nettoförmögenhet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl font-bold tracking-tight tabular-nums lg:text-4xl">
              {formatSEK0(current.net)}
            </span>
            {oldest && netWorth.history.length > 1 && (
              <span
                className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${
                  delta >= 0 ? "text-success" : "text-destructive"
                }`}
              >
                {delta >= 0 ? (
                  <ArrowUpRight className="h-4 w-4" />
                ) : (
                  <ArrowDownRight className="h-4 w-4" />
                )}
                {delta >= 0 ? "+" : ""}
                {formatSEK0(delta)} sedan {formatDate(oldest.date)}
              </span>
            )}
          </div>
          <div className="mt-4">
            <TrendChart data={history} height={170} />
          </div>
        </CardContent>
      </Card>

      {/* Assets / liabilities breakdown */}
      <div className="grid gap-4 sm:grid-cols-2 lg:gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
              Tillgångar
              <span className="text-base font-bold text-success tabular-nums">
                {formatSEK0(current.assets)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {assetRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Inga tillgångar ännu — importera eller lägg till under Tillgångar.
              </p>
            ) : (
              <div className="space-y-2">
                {assetRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="font-medium tabular-nums">{formatSEK0(row.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
              Skulder
              <span className="text-base font-bold text-destructive tabular-nums">
                {formatSEK0(current.liabilities)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {liabilityRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Inga skulder registrerade. Lägg till kreditkort och lån under
                Tillgångar → Konton & kort.
              </p>
            ) : (
              <div className="space-y-2">
                {liabilityRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="font-medium tabular-nums">{formatSEK0(row.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* This month + recurring */}
      <div className="grid gap-4 sm:grid-cols-2 lg:gap-6">
        <Card
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => onNavigate("spending")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Wallet className="h-4 w-4" />
              {month ? formatMonth(month) : "Denna månad"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Utgifter</span>
                  <span className="font-medium text-destructive tabular-nums">
                    {formatSEK0(summary.totalSpending)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Inkomster</span>
                  <span className="font-medium text-success tabular-nums">
                    {formatSEK0(summary.totalIncome)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t pt-2 text-sm">
                  <span className="text-muted-foreground">Netto</span>
                  <span
                    className={`font-semibold tabular-nums ${netThisMonth >= 0 ? "text-success" : "text-destructive"}`}
                  >
                    {netThisMonth >= 0 ? "+" : ""}
                    {formatSEK0(netThisMonth)}
                  </span>
                </div>
                {budgetUsedPct !== null && (
                  <div className="pt-1">
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                      <span>Budget</span>
                      <span className="tabular-nums">
                        {budgetUsedPct}% av {formatSEK0(budgetTotal)}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full transition-all ${
                          budgetUsedPct > 100
                            ? "bg-destructive"
                            : budgetUsedPct > 80
                              ? "bg-warning"
                              : "bg-primary"
                        }`}
                        style={{ width: `${Math.min(budgetUsedPct, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ladda upp kontoutdrag för att se månadens kassaflöde.
              </p>
            )}
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => onNavigate("recurring")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
              Fasta kostnader
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold tabular-nums">
              {formatSEK0(recurring.activeMonthlyTotal)}
              <span className="text-sm font-normal text-muted-foreground"> /mån</span>
            </p>
            {upcoming.length > 0 ? (
              <div className="mt-3 space-y-1.5">
                {upcoming.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-muted-foreground" title={item.name}>
                      {item.name}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {item.nextDate ? formatDate(item.nextDate) : cadenceLabels[item.cadence]} ·{" "}
                      {formatSEK0(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Inga återkommande kostnader hittade ännu.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Investments + crypto quick cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:gap-6">
        <Card
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => onNavigate("assets", "investments")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ChartLine className="h-4 w-4" />
              Investeringar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold tabular-nums">{formatSEK0(investments.totalValue)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {investments.holdings.length > 0
                ? `${investments.holdings.length} innehav` +
                  (investDelta !== null
                    ? ` · ${investDelta >= 0 ? "+" : ""}${formatSEK0(investDelta)} sedan köp`
                    : "")
                : "Importera en positionsexport från t.ex. Avanza"}
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => onNavigate("assets", "crypto")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Bitcoin className="h-4 w-4" />
              Krypto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold tabular-nums">{formatSEK0(crypto.totalValue)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {crypto.holdings.length > 0
                ? `${crypto.holdings.length} innehav · livepriser från CoinGecko`
                : "Lägg till dina kryptoinnehav"}
            </p>
          </CardContent>
        </Card>
      </div>

      {current.breakdown.creditCards > 0 && (
        <Card
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => onNavigate("assets", "accounts")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CreditCard className="h-4 w-4" />
              Kreditkort
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-destructive tabular-nums">
              {formatSEK0(current.breakdown.creditCards)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">Utestående saldo att betala</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
